import { Suspense, lazy, useContext, useEffect, useRef, useState } from 'react';
import { TagSelectButton } from './TagSelectButton';
import {
  EstateStatuses,
  EstateType,
  Estates,
  FilterTypes,
  Status,
} from '../../Enums';
const EstateCardContent = lazy(
  () => import('../ObjectCardComponents/EstateCardContent')
);
import NoSearchResults from './FilterComponents/NoSearchResults';

import {
  useDebounce,
  useKeyPress,
  useOnClickOutside,
  formatLargeNumbers,
  handleUpAndDown,
  focusOnInput,
} from '../../helpers';
import { RemoveFiltersButton } from './RemoveFiltersButton';
import RemoveFilterBtn from './FilterComponents/RemoveFilterBtn';
import SearchFieldBtn from './FilterComponents/SearchFieldBtn';
import { FilterContext } from '../../filterTools';
import {
  shouldShowNoSearchResults,
  shouldShowSearchResults,
} from './AutocompleteStartpageComponent';
import { ForSaleStrings } from '~js/React/types/ForSaleString';
import useAutocomplete from '../CustomHooks/useAutocomplete';
import { FilterEstateTags } from '~js/React/services/generatedApi';

interface Props {
  inputLabel: string;
  activeInput: boolean;
  countySelected: boolean;
  multipleEstateTypesSelected: boolean;
  brokerOrOfficeTypesSelected: boolean;
  commercialOrProject: boolean;
  stickyField?: boolean;
  showMapFilter?: boolean;
}

const SearchField = ({
  inputLabel,
  activeInput,
  countySelected,
  multipleEstateTypesSelected,
  brokerOrOfficeTypesSelected,
  commercialOrProject,
  stickyField = false,
  showMapFilter,
}: Props): JSX.Element => {
  const filterContext = useContext(FilterContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [inputValue, setInputValue] = useState('');
  const debouncedSearchTerm = useDebounce<string>(searchTerm, 200);
  const [acResults, setACResults] = useState<FilterEstateTags | null>();
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showNoSearchResults, setShowNoSearchResults] = useState(false);
  const [freetext, setFreetext] = useState<string>('');

  const ref = useRef<HTMLDivElement>(null);
  const [hideAC, setHideAc] = useState(false);
  const { data: resp } = useAutocomplete(freetext);

  useOnClickOutside(ref, () => setHideAc(true));
  const escPressed = useKeyPress('Escape');

  useEffect(() => {
    document.addEventListener('keydown', handleUpAndDown);
    return () => {
      document.removeEventListener('keydown', handleUpAndDown);
    };
  }, []);
  useEffect(() => {
    setACResults(resp);
  }, [resp]);
  useEffect(() => {
    const query = debouncedSearchTerm.trim();
    if (!query || query.length < 2) {
      setACResults(null);
    } else {
      setFreetext(query);
      setHideAc(false);
    }
  }, [debouncedSearchTerm]);

  function updateLocationTags(
    type: string,
    label?: string,
    id?: string,
    county?: string,
    municipality?: string
  ) {
    const tagsWithPolygonsRemoved = filterContext.tags.filter(
      (x) => !['polygons'].includes(x.type)
    );

    const existingTag = tagsWithPolygonsRemoved.some((x) => x.label === label);

    if (existingTag) {
      filterContext.setTags(
        tagsWithPolygonsRemoved.filter((x) => x.label !== label)
      );
    } else {
      filterContext.setTags([
        ...tagsWithPolygonsRemoved,
        { type, label, id, county, municipality },
      ]);
    }
  }

  function updateEstateTypeTags(estateType?: EstateType) {
    const existingFarmTag = filterContext.tags.filter(
      (x) =>
        x.estateType === EstateType.Agriculture &&
        x.type === FilterTypes.EstateType
    );
    if (existingFarmTag && estateType === EstateType.Agriculture) {
      const filteredArray = filterContext.tags.filter(
        (x) => x.type !== FilterTypes.Alignment && x.estateType !== estateType
      );
      filterContext.setTags(filteredArray);
    } else {
      const existingTag = filterContext.tags.some(
        (x) => x.estateType === estateType && x.type === FilterTypes.EstateType
      );

      if (existingTag) {
        filterContext.setTags(
          filterContext.tags.filter((x) => x.estateType !== estateType)
        );
      } else {
        filterContext.setTags([
          ...filterContext.tags,
          { type: FilterTypes.EstateType, estateType },
        ]);
      }
    }
  }

  function updateRangeTags(
    type: string,
    minVal: number,
    maxVal: number,
    name?: string
  ) {
    const existingTag = filterContext.tags.some(
      (x) => x.name === name && x.type === type
    );
    if (existingTag) {
      filterContext.setTags(filterContext.tags.filter((x) => x.name !== name));
    } else {
      filterContext.setTags([
        ...filterContext.tags,
        { name, minVal, maxVal, type: type },
      ]);
    }
  }

  function updateExtraCritTags(type: string, id?: string, text?: string) {
    const existingTag = filterContext.tags.some(
      (x) => x.id === id && x.text === text && x.type === type
    );

    if (existingTag) {
      filterContext.setTags(filterContext.tags.filter((x) => x.id !== id));
    } else {
      filterContext.setTags([
        ...filterContext.tags,
        { type: FilterTypes.ExtraCritera, id, text },
      ]);
    }
  }

  function updatePolygonsTag(type: string) {
    const existingTag = filterContext.tags.some((x) => x.type === type);
    if (existingTag) {
      filterContext.setTags(filterContext.tags.filter((x) => x.type !== type));
    }
  }

  function updateAlignmentTags(id?: string) {
    const existingTag = filterContext.tags.some((x) => x.id === id);

    if (existingTag) {
      filterContext.setTags(filterContext.tags.filter((x) => x.id !== id));
    } else {
      filterContext.setTags([
        ...filterContext.tags,
        { type: FilterTypes.Alignment, id },
      ]);
    }
  }

  function updateFromKeyPress(event: any) {
    function updateFromAcResults() {
      if (!!acResults?.municipalities[0]) {
        updateLocationTags(
          FilterTypes.Municipality,
          `${acResults?.municipalities[0].name} (Kommun)`,
          acResults?.municipalities[0].name
        );
        setInputValue('');
        setSearchTerm('');
      } else if (!!acResults?.areas[0]) {
        updateLocationTags(
          FilterTypes.Area,
          `${acResults?.areas[0].name} (${acResults?.areas[0].municipality} kommun)`,
          acResults?.areas[0].id
        );
        setInputValue('');
        setSearchTerm('');
      } else if (!!acResults?.counties[0]) {
        updateLocationTags(
          FilterTypes.County,
          `${acResults?.counties[0].name} (Län)`,
          acResults?.counties[0].name
        );
        setInputValue('');
        setSearchTerm('');
      }
      return;
    }
    if (document !== null) {
      if (event.key === 'Enter') {
        updateFromAcResults();
      }
    }
  }

  function updateFromKeyDown(event: any, currentInputValue: any) {
    if (filterContext.tags.length > 0) {
      const listFreeFromShowOnly = filterContext.tags.filter(
        (x) =>
          x.type !== FilterTypes.ShowOnlyHeadStart &&
          x.type !== FilterTypes.ShowSold
      );
      if (listFreeFromShowOnly.length > 0) {
        let itemToCheckForRemove =
          filterContext.tags[filterContext.tags.length - 1];
        if (event.key === 'Backspace' && currentInputValue === '') {
          if (
            itemToCheckForRemove.type === FilterTypes.ShowSold ||
            itemToCheckForRemove.type === FilterTypes.ShowOnlyHeadStart
          ) {
            if (filterContext.tags.length > 1) {
              itemToCheckForRemove =
                filterContext.tags[filterContext.tags.length - 2];
              if (
                itemToCheckForRemove.type === FilterTypes.ShowSold ||
                itemToCheckForRemove.type === FilterTypes.ShowOnlyHeadStart
              ) {
                if (filterContext.tags.length > 2) {
                  itemToCheckForRemove =
                    filterContext.tags[filterContext.tags.length - 3];
                }
              }
            }
          }
          if (itemToCheckForRemove.type === FilterTypes.EstateType) {
            filterContext.setTags(
              filterContext.tags.filter(
                (x) => x.estateType !== itemToCheckForRemove.estateType
              )
            );
          } else if (itemToCheckForRemove.type === FilterTypes.RangeTag) {
            filterContext.setTags(
              filterContext.tags.filter(
                (x) => x.name !== itemToCheckForRemove.name
              )
            );
          } else {
            filterContext.setTags(
              filterContext.tags.filter((x) => x.id !== itemToCheckForRemove.id)
            );
          }
        }
      }
    }
  }

  function updateStatusTags(type: string) {
    const existingStatusTag = filterContext.tags.some((x) => x.type === type);
    if (existingStatusTag) {
      filterContext.setTags(filterContext.tags.filter((x) => x.type !== type));
    } else {
      filterContext.setTags([
        ...filterContext.tags,
        { type: type, isChecked: false },
      ]);
    }
  }

  useEffect(() => {
    if (escPressed) {
      setHideAc(true);
    }
  }, [escPressed]);

  useEffect(() => {
    setShowSearchResults(shouldShowSearchResults(hideAC, acResults));
    setShowNoSearchResults(shouldShowNoSearchResults(hideAC, acResults));
  }, [hideAC, acResults]);

  useEffect(() => {
    if (stickyField) {
      const el = document.querySelector('.sticky-field') as Element | null;

      if (el) {
        const observer = new IntersectionObserver(
          ([e]) => {
            const shouldAddClasses = e.intersectionRatio < 1;

            e.target.classList.toggle('shadow-bs60', shouldAddClasses);

            if (shouldAddClasses && !showMapFilter) {
              e.target.classList.add('pt-14');
            } else {
              e.target.classList.remove('pt-14');
            }

            e.target.classList.toggle('isSticky', shouldAddClasses);
          },
          { threshold: [1] }
        );

        observer.observe(el);

        return () => observer.disconnect();
      }
    }
  }, [stickyField, showMapFilter]);

  return (
    <div
      className={`bg-white ${
        stickyField
          ? 'sticky lg:static top-[-1px] left-0 z-[11] py-2 sticky-field'
          : ''
      }`}
    >
      <div className="z-[10] flex flex-wrap bg-grey50 mr-3 pl-2 border-white rounded-r-3xl">
        <div className="flex flex-wrap flex-grow py-2 w-auto search-field">
          {filterContext?.tags && (
            <div className="flex flex-wrap gap-2 mr-2">
              {filterContext.tags.map((x, i) => {
                if (
                  !activeInput &&
                  (x.type === FilterTypes.County ||
                    x.type === FilterTypes.Municipality ||
                    x.type === FilterTypes.Area)
                ) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.label}
                      onClick={() => {
                        updateLocationTags(
                          x.type,
                          x.label,
                          x.id,
                          x.county,
                          x.municipality
                        );
                      }}
                    />
                  );
                } else if (
                  activeInput &&
                  countySelected &&
                  x.type === FilterTypes.County
                ) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.label}
                      onClick={() => {
                        updateLocationTags(
                          x.type,
                          x.label,
                          x.id,
                          x.county,
                          x.municipality
                        );
                      }}
                    />
                  );
                } else if (
                  activeInput &&
                  (x.type === FilterTypes.Municipality ||
                    x.type === FilterTypes.Area)
                ) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.label}
                      onClick={() => {
                        updateLocationTags(
                          x.type,
                          x.label,
                          x.id,
                          x.county,
                          x.municipality
                        );
                      }}
                    />
                  );
                } else if (!activeInput && x.type === FilterTypes.EstateType) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.estateType}
                      onClick={() => {
                        updateEstateTypeTags(x.estateType);
                      }}
                    />
                  );
                } else if (activeInput && x.type === FilterTypes.EstateType) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.estateType}
                      warningMsg={multipleEstateTypesSelected}
                      errorMsg={commercialOrProject}
                      onClick={() => {
                        updateEstateTypeTags(x.estateType);
                      }}
                    />
                  );
                } else if (x.type === FilterTypes.RangeTag) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      onClick={() => {
                        updateRangeTags(x.type, 0, 0, x.name);
                      }}
                    >
                      {x.name === 'Pris' &&
                      x.minVal !== undefined &&
                      x.maxVal !== undefined ? (
                        <>
                          {x.name}: {formatLargeNumbers(x.minVal)}-
                          {formatLargeNumbers(x.maxVal)}
                          {x.maxVal === 15000000 ? '+' : ''} SEK
                        </>
                      ) : x.name === 'Avgift' &&
                        x.minVal !== undefined &&
                        x.maxVal !== undefined ? (
                        <>
                          {x.name}: {formatLargeNumbers(x.minVal)}-
                          {formatLargeNumbers(x.maxVal)}
                          {x.maxVal === 10000 ? '+' : ''} SEK
                        </>
                      ) : x.name === 'Boarea' ? (
                        <>
                          {x.name}: {x.minVal}-{x.maxVal}
                          {x.maxVal === 200 ? '+' : ''} m²
                        </>
                      ) : x.name === 'Tomtarea' &&
                        x.minVal !== undefined &&
                        x.maxVal !== undefined ? (
                        <>
                          {x.name}: {formatLargeNumbers(x.minVal)}-
                          {formatLargeNumbers(x.maxVal)}
                          {x.maxVal === 15000 ? '+' : ''} m²
                        </>
                      ) : (
                        <>
                          {x.name}: {x.minVal}-{x.maxVal}
                          {x.maxVal === 10 ? '+' : ''}
                        </>
                      )}
                    </RemoveFilterBtn>
                  );
                } else if (x.type === FilterTypes.ExtraCritera) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.text}
                      onClick={() => {
                        updateExtraCritTags('extraCriteria', x.id, x.text);
                      }}
                    />
                  );
                } else if (x.type === FilterTypes.Alignment) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.id}
                      onClick={() => {
                        updateAlignmentTags(x.id);
                      }}
                    />
                  );
                } else if (
                  x.type === FilterTypes.Broker ||
                  x.type === FilterTypes.Office
                ) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      errorMsg={brokerOrOfficeTypesSelected}
                      onClick={() => {
                        updateStatusTags(x.type);
                      }}
                    >
                      {Object.keys(x.label).map((key: any) => x.label[key])}
                    </RemoveFilterBtn>
                  );
                } else if (
                  x.type === FilterTypes.ShowGreenEstates &&
                  x.isChecked
                ) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.label}
                      errorMsg={brokerOrOfficeTypesSelected}
                      onClick={() => {
                        updateStatusTags(x.type);
                      }}
                    >
                      {x.label}
                    </RemoveFilterBtn>
                  );
                } else if (x.type === FilterTypes.ShowBidding && x.isChecked) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.label}
                      errorMsg={brokerOrOfficeTypesSelected}
                      onClick={() => {
                        updateStatusTags(x.type);
                      }}
                    >
                      {x.label}
                    </RemoveFilterBtn>
                  );
                } else if (
                  x.type === FilterTypes.ShowOnlyHeadStart &&
                  x.isChecked
                ) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.label}
                      errorMsg={brokerOrOfficeTypesSelected}
                      onClick={() => {
                        updateStatusTags(x.type);
                      }}
                    >
                      {Status.headStartPlural}
                    </RemoveFilterBtn>
                  );
                } else if (x.type === FilterTypes.ShowSold && x.isChecked) {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.label}
                      onClick={() => {
                        updateStatusTags(x.type);
                      }}
                    >
                      {Status.sold}
                    </RemoveFilterBtn>
                  );
                } else if (x.type === 'polygons') {
                  return (
                    <RemoveFilterBtn
                      key={i}
                      index={i}
                      text={x.label}
                      onClick={() => {
                        updatePolygonsTag(x.type);
                      }}
                    />
                  );
                } else {
                  return null;
                }
              })}
              {activeInput &&
                (filterContext.tags.length === 0 ||
                  (filterContext.tags.filter(
                    (x) =>
                      x.type === 'showSold' ||
                      x.type === 'showOnlyHeadStart' ||
                      x.type === 'sortType'
                  ).length > 0 &&
                    filterContext.tags.filter(
                      (x) =>
                        x.type !== 'showSold' &&
                        x.type !== 'showOnlyHeadStart' &&
                        x.type !== 'sortType'
                    ).length === 0)) && (
                  <div className="mt-2 text-grey300">
                    För att skapa en bevakning måste du ha valt några
                    sökparametrar.
                  </div>
                )}
            </div>
          )}

          {!activeInput && (
            <div
              id="searchfield-input-wrapper"
              className="relative flex flex-grow py-0 mdplus:py-2 pr-6"
            >
              <input
                aria-label={Estates.SearchPlaceholder}
                name="searchfield-input"
                id="searchfield-input"
                className="bg-transparent py-2 lg:py-1 focus:outline-none w-full text-black500 md:text-base"
                type="search"
                value={inputValue}
                placeholder={inputLabel}
                autoComplete="off"
                onClick={() => setHideAc(false)}
                onChange={(e) => {
                  setSearchTerm(e.currentTarget.value);
                  setInputValue(e.currentTarget.value);
                }}
                disabled={activeInput}
                onKeyPress={updateFromKeyPress}
                onKeyDown={(e) => {
                  updateFromKeyDown(e, inputValue);
                }}
              />
              {filterContext.tags.filter(
                (x) =>
                  x.type !== 'sortType' && x.type !== FilterTypes.AllEstates
              ).length > 0 ? (
                <RemoveFiltersButton
                  resetTags={() => {
                    filterContext.setTags([
                      {
                        type: FilterTypes.AllEstates,
                        isChecked: true,
                        label: 'Alla bostäder',
                      },
                    ]);
                  }}
                />
              ) : (
                <SearchFieldBtn />
              )}
            </div>
          )}
        </div>
      </div>
      {showSearchResults && (
        <div className="relative" ref={ref}>
          <div
            id="ac-result-list"
            className="left-0 z-50 absolute bg-white shadow-bs80 mt-2 border-t-grey100 w-[calc(100%-0.75rem)] max-h-xl overflow-hidden overflow-y-auto"
          >
            {!!acResults?.municipalities.length && (
              <div className="outline-none">
                {acResults.municipalities?.map((x, i) => (
                  <TagSelectButton
                    classToChild={i === 0 ? `bg-grey50` : ''}
                    onClick={() => {
                      updateLocationTags(
                        'munici',
                        `${x.name} (Kommun)`,
                        x.name
                      );
                      setHideAc(true);
                      setInputValue('');
                      setSearchTerm('');
                      focusOnInput();
                    }}
                    key={x.name}
                    text={x.name}
                    type="Kommun"
                    handleKeyPress={() => {
                      updateLocationTags(
                        'munici',
                        `${x.name} (Kommun)`,
                        x.name
                      );
                      setInputValue('');
                      setSearchTerm('');
                      focusOnInput();
                    }}
                  />
                ))}
              </div>
            )}
            {!!acResults?.areas.length && (
              <div className="outline-none">
                {acResults.areas?.map((x, i) => (
                  <TagSelectButton
                    classToChild={
                      i === 0 && !acResults.municipalities.length
                        ? `bg-grey50`
                        : ''
                    }
                    onClick={() => {
                      updateLocationTags(
                        'area',
                        `${x.name} (${x.municipality} kommun)`,
                        x.id
                      );
                      setHideAc(true);
                      setInputValue('');
                      setSearchTerm('');
                      focusOnInput();
                    }}
                    key={x.id}
                    text={x.name}
                    type={`${x.municipality} kommun`}
                    handleKeyPress={() => {
                      updateLocationTags(
                        'area',
                        `${x.name} (${x.municipality} kommun)`,
                        x.id
                      );
                      setInputValue('');
                      setSearchTerm('');
                      focusOnInput();
                    }}
                  />
                ))}
              </div>
            )}
            {!!acResults?.counties.length && (
              <div className="outline-none">
                {acResults.counties?.map((x, i) => (
                  <TagSelectButton
                    classToChild={
                      i === 0 &&
                      acResults.municipalities.length === 0 &&
                      acResults.areas.length === 0
                        ? `bg-grey50`
                        : ''
                    }
                    onClick={() => {
                      updateLocationTags('county', `${x.name} (Län)`, x.name);
                      setHideAc(true);
                      setInputValue('');
                      setSearchTerm('');
                      focusOnInput();
                    }}
                    key={x.name}
                    text={x.name}
                    type="Län"
                    handleKeyPress={() => {
                      updateLocationTags('county', `${x.name} (Län)`, x.name);
                      setInputValue('');
                      setSearchTerm('');
                      focusOnInput();
                    }}
                  />
                ))}
              </div>
            )}
            {!!acResults?.estates.length && (
              <>
                <h3 className="my-3 ml-3">{ForSaleStrings.Estates}</h3>
                {acResults.estates?.map(
                  (x) =>
                    x.estateType !== EstateType.Commercial &&
                    x.status !== EstateStatuses.Sold && (
                      <a
                        className="block hover:bg-grey50 px-3 py-3 border-grey75 border-b outline-none acResult"
                        href={x.url}
                        key={x.url}
                      >
                        <div className="flex items-center">
                          <div className="rounded-md max-w-1/4 h-20 md:h-[6rem] overflow-hidden">
                            <img
                              src={
                                x.headerImage
                                  ? x.headerImage
                                  : '/Images/fallbacks/hero-image-fallback.jpg'
                              }
                              className="w-full h-full object-cover"
                              alt={`${x.streetAddress}`}
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-grow -mx-0 md:mx-0 -my-6 md:-my-4 min-w-1/4">
                            <Suspense>
                              <EstateCardContent estate={x} />
                            </Suspense>
                          </div>
                        </div>
                      </a>
                    )
                )}
              </>
            )}
          </div>
        </div>
      )}
      {showNoSearchResults && (
        <Suspense>
          <NoSearchResults searchTerm={searchTerm} />
        </Suspense>
      )}
    </div>
  );
};
export default SearchField;
